import { useCallback, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { ModelRun } from '@elementary/common-ui/types';
import {
  formatShortDateTime,
  localizeDate,
} from '@elementary/common-ui/utils/datetime';
import { Stack, Box } from '@mui/material';
import dayjs from 'dayjs';
import Humanize from 'humanize-plus';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  Scatter,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import RunsChartTooltip from './ModelRunsChartTooltip';

const barColorMap = {
  error: 'red',
  success: 'green',
} as any;

const barHoverColorMap = {
  error: 'rgba(255, 101, 91, 0.2)',
  success: 'rgba(66, 203, 154, 0.2)',
} as any;

const Wrapper = styled(Stack)`
  border-radius: 8px;
  background-color: ${({ theme }) => theme.palette.common.gray3};
  width: 100%;
`;

const DotWrapper = styled(Stack)`
  width: 100%;
  height: 100%;
  justify-content: center;
  align-items: center;
`;

const DotElement = styled(Box)<{ status: string }>`
  height: 8px;
  width: 8px;
  background: ${({ theme, status }) =>
    theme.palette.common[barColorMap[status]]};
  border: 1px solid transparent;
  border-radius: 4px;
  &:hover {
    border: ${({ status }) => `3px solid ${barHoverColorMap[status]}`};
    box-sizing: content-box;
    border-radius: 8px;
    background-clip: padding-box;
    -webkit-background-clip: padding-box;
  }
`;

const renderTooltip = (props: any) => <RunsChartTooltip {...props} />;

const ChartDot = ({ cx, cy, payload }: any) => (
  <foreignObject x={cx - 8} y={cy - 8} height={16} width={16}>
    <DotWrapper>
      <DotElement status={payload.status} />
    </DotWrapper>
  </foreignObject>
);

const ModelRunsChart = ({ data }: { data: ModelRun[] }) => {
  const scrollContainerRef = useRef<any>();

  const normalizedData = useMemo(
    () =>
      Array.isArray(data)
        ? [...data]
            .sort(
              (a, b) =>
                dayjs(a.time_utc).valueOf() - dayjs(b.time_utc).valueOf(),
            )
            .map((item) => ({
              ...item,
              execution_time: item.execution_time || 0,
            }))
        : [],
    [data],
  );

  const getErrorExecTime = useCallback(
    (index: number) => {
      const prevValue = normalizedData[index - 1]?.execution_time || 0;
      const nextValue = normalizedData[index + 1]?.execution_time || 0;
      return (prevValue + nextValue) / 2;
    },
    [normalizedData],
  );

  const scatterLineData = useMemo(
    () =>
      normalizedData.map((item, index: number) =>
        item.status === 'error'
          ? { ...item, execution_time: getErrorExecTime(index) }
          : item,
      ),
    [getErrorExecTime, normalizedData],
  );

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft =
        scrollContainerRef.current?.scrollWidth;
    }
  }, []);

  return (
    <Wrapper direction="row" aria-label="Model runs chart">
      <ComposedChart
        data={normalizedData}
        margin={{ bottom: 46, top: 30, left: 14 }}
        height={300}
        width={80}
      >
        <YAxis
          axisLine={false}
          tickLine={false}
          tickMargin={6}
          dataKey="execution_time"
          tick={{ fontSize: 13 }}
          unit="s"
          tickFormatter={(value) => Humanize.compactInteger(value)}
        />
      </ComposedChart>

      <Box
        sx={{ overflowX: 'auto', overflowY: 'hidden', flex: 1 }}
        ref={scrollContainerRef}
      >
        <ResponsiveContainer
          minWidth={normalizedData.length * 40}
          width="99%"
          height={300}
        >
          <ComposedChart
            data={normalizedData}
            margin={{ top: 28, right: 20, bottom: 14 }}
          >
            <CartesianGrid stroke="#E6E8EC" />

            <Scatter
              data={scatterLineData}
              shape={() => <div />}
              line={{
                stroke: '#4992FF',
                strokeDasharray: 4,
              }}
              lineType="fitting"
            />

            <XAxis
              axisLine={false}
              tickLine={false}
              tickMargin={6}
              padding={{ left: 50 }}
              tickFormatter={(value) =>
                dayjs(value).isValid() && value !== 0
                  ? formatShortDateTime(localizeDate(value))
                  : ''
              }
              dataKey="time_utc"
              tick={{ fontSize: 11 }}
            />

            <YAxis
              axisLine={false}
              tickLine={false}
              tickMargin={6}
              dataKey="execution_time"
              tick={{ fontSize: 13 }}
              hide
              tickFormatter={(value) => Humanize.compactInteger(value)}
            />

            <Tooltip
              content={(props: any) => renderTooltip(props)}
              cursor
              isAnimationActive
              wrapperStyle={{
                outline: 0,
              }}
            />

            <Line
              dataKey="execution_time"
              stroke="none"
              dot={<ChartDot />}
              activeDot={false}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>
    </Wrapper>
  );
};

export default ModelRunsChart;
